import cx from 'classnames';
import React, { useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

export interface ContainerProps<OptionType extends CascaderOption>
  extends BoxProps {
  children: React.ReactNode;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalContainer = <OptionType extends CascaderOption>({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { cascaderStyles, comboboxApi, getCommonProps, className } =
    useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomContainer = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.CONTAINER),
    [getComponent],
  );

  return (
    <CustomContainer
      getCommonProps={getCommonProps}
      __css={cascaderStyles.container}
      {...comboboxApi.getRootProps()}
      className={cx(className, 'cascader-container')}
    >
      {children}
    </CustomContainer>
  );
};

export const Container = <OptionType extends CascaderOption>({
  children,
  getCommonProps: _,
  ...rest
}: ContainerProps<OptionType>) => {
  return <Box {...rest}>{children}</Box>;
};
